import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import Modal from '../../components/Modal';

const REGIONS = ['Fort Worth', 'Dallas', 'Denton'];
const STATUSES = ['active', 'down', 'in_shop', 'out_of_service'];

const STATUS_COLORS = {
  active: '#10b981',
  down: '#ef4444',
  in_shop: '#f59e0b',
  out_of_service: '#6b7280',
};

const STATUS_LABELS = {
  active: 'Active',
  down: 'Down',
  in_shop: 'In Shop',
  out_of_service: 'Out of Service',
};

const EMPTY_FORM = {
  unit_number: '',
  vin: '',
  license_plate: '',
  make: '',
  model: '',
  year: '',
  region: '',
  assigned_driver_id: '',
  current_mileage: '',
  status: 'active',
  notes: '',
};

export default function TruckManagementPage() {
  const { can } = useAuth();
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTruck, setEditingTruck] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [truckData, driverData] = await Promise.all([
        api.getTrucks(),
        api.getDrivers().catch(() => []),
      ]);
      setTrucks(Array.isArray(truckData) ? truckData : (truckData.trucks || []));
      setDrivers(Array.isArray(driverData) ? driverData : (driverData.drivers || []));
    } catch (err) {
      setError(err.message || 'Failed to load trucks');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  function openAdd() {
    setEditingTruck(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  }

  function openEdit(truck) {
    setEditingTruck(truck);
    setForm({
      unit_number: truck.unit_number || '',
      vin: truck.vin || '',
      license_plate: truck.license_plate || '',
      make: truck.make || '',
      model: truck.model || '',
      year: truck.year || '',
      region: truck.region || '',
      assigned_driver_id: truck.assigned_driver_id || '',
      current_mileage: truck.current_mileage || '',
      status: truck.status || 'active',
      notes: truck.notes || '',
    });
    setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        current_mileage: form.current_mileage ? Number(form.current_mileage) : null,
        assigned_driver_id: form.assigned_driver_id ? Number(form.assigned_driver_id) : null,
      };
      if (editingTruck) {
        await api.updateTruck(editingTruck.id, payload);
      } else {
        await api.createTruck(payload);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to save truck');
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    try {
      await api.deleteTruck(id);
      setDeleteConfirm(null);
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete truck');
      setDeleteConfirm(null);
    }
  }

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  function getDriverName(driverId) {
    if (!driverId) return '—';
    const d = drivers.find(dr => dr.id === driverId);
    return d ? d.full_name || d.username : '—';
  }

  if (!can('manage_trucks')) {
    return <div className="page-content"><p>You do not have permission to manage trucks.</p></div>;
  }

  return (
    <div className="truck-table">
      <div className="maint-types__header">
        <h1>Truck Management</h1>
        <button className="btn btn-gold" onClick={openAdd}>+ Add Truck</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading-spinner">Loading trucks...</div>
      ) : (
        <div className="table-responsive">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Unit #</th>
                <th>Make / Model</th>
                <th className="hide-mobile">Year</th>
                <th>Region</th>
                <th>Assigned Driver</th>
                <th className="hide-mobile">Mileage</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {trucks.length === 0 ? (
                <tr><td colSpan="8" className="text-center">No trucks found. Add one to get started.</td></tr>
              ) : trucks.map(truck => (
                <tr key={truck.id}>
                  <td><strong>{truck.unit_number}</strong></td>
                  <td>{[truck.make, truck.model].filter(Boolean).join(' ') || '—'}</td>
                  <td className="hide-mobile">{truck.year || '—'}</td>
                  <td>{truck.region || '—'}</td>
                  <td>{getDriverName(truck.assigned_driver_id)}</td>
                  <td className="hide-mobile">{truck.current_mileage ? truck.current_mileage.toLocaleString() : '—'}</td>
                  <td>
                    <span
                      className="status-badge"
                      style={{ backgroundColor: STATUS_COLORS[truck.status] || '#6b7280', color: '#fff' }}
                    >
                      {STATUS_LABELS[truck.status] || truck.status}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-sm btn-outline" onClick={() => openEdit(truck)}>Edit</button>
                    {' '}
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(truck)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <Modal title={editingTruck ? 'Edit Truck' : 'Add Truck'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSave}>
            <div className="form-grid">
              <div className="form-group">
                <label>Unit Number *</label>
                <input name="unit_number" value={form.unit_number} onChange={handleChange} required className="form-input" />
              </div>
              <div className="form-group">
                <label>VIN</label>
                <input name="vin" value={form.vin} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>License Plate</label>
                <input name="license_plate" value={form.license_plate} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Make</label>
                <input name="make" value={form.make} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Model</label>
                <input name="model" value={form.model} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Year</label>
                <input name="year" type="number" value={form.year} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Region</label>
                <select name="region" value={form.region} onChange={handleChange} className="form-input">
                  <option value="">Select Region</option>
                  {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Assigned Driver</label>
                <select name="assigned_driver_id" value={form.assigned_driver_id} onChange={handleChange} className="form-input">
                  <option value="">Unassigned</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.id}>{d.full_name || d.username}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Current Mileage</label>
                <input name="current_mileage" type="number" value={form.current_mileage} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={form.status} onChange={handleChange} className="form-input">
                  {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label>Notes</label>
              <textarea name="notes" value={form.notes} onChange={handleChange} className="form-input" rows={3} />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-gold" disabled={saving}>
                {saving ? 'Saving...' : (editingTruck ? 'Update Truck' : 'Add Truck')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <Modal title="Delete Truck" onClose={() => setDeleteConfirm(null)}>
          <p>Are you sure you want to delete truck <strong>{deleteConfirm.unit_number}</strong>? This action cannot be undone.</p>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
